import type { Skill, SkillImportProgress, SkillImportResult } from '../types/skill';
import { formatSkillImportError, MAX_LOCAL_SKILL_ARCHIVE_BYTES } from './skill';
import { i18nService } from './i18n';

/** 一个用户选中的本地归档；文件名仅用于展示，不作服务端路径。 */
export interface SkillImportArchive { name: string; blob: Blob }
export type VisibleSkillImportProgress = Omit<SkillImportProgress, 'phase'> & {
  sourceLabel: string;
  phase: SkillImportProgress['phase'] | 'packaging' | 'uploading';
};
export interface SkillBatchSummary { installed: number; skipped: number; failed: number; errors: string[] }
/** 批次依赖由页面注入；signal 固定于开始时的账号/页面生命周期。 */
export interface SkillBatchOptions {
  archives: SkillImportArchive[];
  signal: AbortSignal;
  upload: (archive: Blob, requestId: string, options: { force?: boolean; skipDuplicates?: boolean }, onProgress: (value: SkillImportProgress) => void) => Promise<SkillImportResult>;
  confirmOverwrite: (names: string[]) => Promise<boolean>;
  onProgress: (value: VisibleSkillImportProgress) => void;
  onSkills: (skills: Skill[]) => void;
}

/** 串行执行并隔离单包失败；每个异步边界检查取消，禁止旧账号续传或应用迟到结果。 */
export async function runSkillImportBatch(options: SkillBatchOptions): Promise<SkillBatchSummary> {
  const { signal } = options;
  const summary: SkillBatchSummary = { installed: 0, skipped: 0, failed: 0, errors: [] };
  for (const archive of options.archives) {
    signal.throwIfAborted();
    const sourceLabel = archive.name.split(/[\\/]/).pop() || archive.name;
    if (archive.blob.size > MAX_LOCAL_SKILL_ARCHIVE_BYTES) {
      summary.failed++;
      summary.errors.push(`${sourceLabel}: ${i18nService.t('skillImportPackageTooLarge')}`);
      continue;
    }
    /** 进度订阅可能晚到，必须和结果使用同一取消边界。 */
    const onProgress = (progress: SkillImportProgress) => {
      if (!signal.aborted) options.onProgress({ ...progress, sourceLabel });
    };
    /** 重复确认只重传当前包，始终重新检查旧批次是否已失效。 */
    const upload = (overwrite: { force?: boolean; skipDuplicates?: boolean }) => {
      signal.throwIfAborted();
      options.onProgress({ sourceLabel, phase: 'uploading', processed: 0, installed: 0 });
      return options.upload(archive.blob, crypto.randomUUID(), overwrite, onProgress);
    };
    try {
      let result = await upload({});
      signal.throwIfAborted();
      if (result.errorCode === 'DUPLICATE') {
        const overwrite = await options.confirmOverwrite(result.duplicateSkills?.map(skill => skill.name || skill.id) || []);
        signal.throwIfAborted();
        result = await upload(overwrite ? { force: true } : { skipDuplicates: true });
        signal.throwIfAborted();
      }
      summary.installed += result.installedCount ?? 0;
      summary.skipped += result.skippedCount ?? 0;
      summary.failed += result.failedCount ?? (result.success ? 0 : 1);
      if (result.skills) options.onSkills(result.skills);
      if (!result.success) summary.errors.push(`${sourceLabel}: ${formatSkillImportError(result)}`);
    } catch {
      signal.throwIfAborted();
      summary.failed++;
      summary.errors.push(`${sourceLabel}: ${i18nService.t('skillImportFailed')}`);
    }
  }
  return summary;
}
